import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { RmsModule } from '@rms';
import { Column, HeaderButton, TableIcon } from '@interfaces';
import { WebService, lastValueFrom, RmsMessageService, PromptService } from '@services';
import { ExcelWriterService, ExcelReaderService, ExcelColumnSpec, InventoryDataService } from '@services'; // adjust path as needed

const schema: ExcelColumnSpec[] = [
  { header: 'Carrier', field: 'PreferredSSL', type: 'string', required: true },
  { header: 'Validity From', field: 'DateFrom', type: 'isoDate', required: true },
  { header: 'Validity To', field: 'DateGoodThru', type: 'isoDate', required: true },
  {
    header: 'Destination',
    field: 'DestinationPort',
    type: 'string',
    required: true
  },
  {
    header: 'Base Freight',
    field: 'SteamshipRate',
    type: 'number',
    required: true,
    transform: (n) => Number.isFinite(n) ? Math.round(n) : 0
  },
  {
    header: 'Country of Origin',
    field: 'OriginName',
    type: 'string',
    required: true
  },
  {
    header: 'Country',
    field: 'CountryName',
    type: 'string',
    required: true
  },
  {
    header: 'Destination WHSE',
    field: 'DestinationID',
    type: 'int',
    required: true
  },
  {
    header: 'Destination Type',
    field: 'DestinationType',
    type: 'string',
    required: false
  },
  {
    header: 'Routing',
    field: 'Routing',
    type: 'string',
    required: false
  },
  {
    header: 'Contract',
    field: 'Contract',
    type: 'string',
    required: false
  },
  {
    header: 'Origin Port',
    field: 'OriginPort',
    type: 'string',
    required: false
  },
  {
    header: 'Equipment',
    field: 'CNTR',
    type: 'string',
    required: false
  }
];

type ImportIssue = {
  row: number;        // Excel row number (1-based)
  column: string;     // Excel header text
  message: string;
  value?: any;
};

type RateVariance = {
  row: number;               // Excel row number
  origin: string;
  destination: string;
  whse: number;
  uploaded: number;
  existing: number;
  diff: number;
  existingId?: any;
};

@Component({
  selector: 'app-ssl-rate',
  standalone: true,
  imports: [RmsModule],
  templateUrl: './ssl-rate.component.html',
  styleUrl: './ssl-rate.component.scss'
})
export class SslRateComponent implements OnInit {

  // ✅ ONLY ACTIVE rates are shown
  OrginRatesList: any[] = [];

  RatesColumns: Column[] = [];

  @ViewChild('ratesFileInput', { static: false }) ratesFileInput!: ElementRef<HTMLInputElement>;

  HeaderButtons: HeaderButton[] = [];
  CountriesList: any[] = [];
  CurrentRates: any[] = [];

  Icons: TableIcon[] = [
    { action: (row) => { this.UpdateRowRate(row) }, icon: 'edit', title: 'Update Rate' }
  ];

  constructor(
    private webService: WebService,
    private excelSvc: ExcelWriterService,
    private excelReader: ExcelReaderService,
    private message: RmsMessageService,
    private data: InventoryDataService,
    private prompt: PromptService
  ) { }

  async ngOnInit() {
    const all = await this.GetRatesList();
    this.CurrentRates = all;

    const data = await this.data.getData({ keys: ['Countries'] });
    this.CountriesList = data['Countries'] ?? [];

    // ✅ show only active
    this.OrginRatesList = all.filter(r => r.Active);
    this.RatesColumns = await this.CreateRatesColumns();
  }

  async GetRatesList(): Promise<any[]> {
    const rows = await lastValueFrom(this.webService.getWebCall('data', 'coffee/origin-rates'));
    return (rows ?? []).map((r: any) => this.normalizeRow(r));
  }

  async CreateRatesColumns(): Promise<Column[]> {
    return [
      { key: 'CountryName', label: 'Country' },
      // Use DestinationPort so UI matches what upload provides
      { key: 'DestinationPort', label: 'Destination' },
      { key: 'RateDateTime', label: 'Date Entered', type: 'date' },
      { key: 'DateFrom', label: 'From', type: 'date' },
      { key: 'DateGoodThru', label: 'Thru', type: 'date' },
      { key: 'SteamshipRate', label: 'Rate', type: 'currency' },
      { key: 'PreferredSSL', label: 'Preferred SSL' },
      { key: 'PreferredRate', label: 'Preferred', type: 'checkbox', edit: true, onAction: (row) => { this.onPreferredRateChange(row) } }
    ];
  }

  // =========================
  // Upload flow
  // =========================
  startUpload() {
    this.ratesFileInput?.nativeElement?.click();
  }

  async onPreferredRateChange(row: any) {
    try {
      const payload = this.normalizeForMerge({
        OriginRateID: row.OriginRateID,
        PreferredRate: row.PreferredRate
      });

      await lastValueFrom(this.webService.getWebCall('inventory', 'merge_sslrates', { body: payload }));
      await this.ngOnInit();
      this.message.success('Preferred rate updated successfully');
    } catch (err: any) {
      this.message.error('Failed to update preferred rate: ' + err?.error?.originalError?.info?.message);
    }
  }

  async onRatesFileDropped(fileOrFiles: File | File[]) {
    const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;
    if (!file) return;

    const { rows, errors } = await this.excelReader.readFromFile<any>(
      file,
      schema,
      { headerRow: 'auto', skipEmptyRows: true }
    );

    if (errors?.length) {
      // Keep in console for debugging; we do our own strict validation below
      console.warn('Excel import warnings/errors', errors);
    }

    // ✅ Step A: strict validation with precise row/column feedback
    const issues = this.validateUploadedRows(rows);
    if (issues.length) {
      this.message.error('Upload blocked due to invalid Excel values:\n' + this.formatIssues(issues, 25));
      return;
    }

    const todayISO = new Date().toISOString().substring(0, 10);

    // ✅ build lookup once
    const countryLookup = this.buildCountryLookup();

    const uploaded = rows.map(r => {
      const countryNameRaw = r.CountryName ?? '';
      const countryKey = this.normText(countryNameRaw);
      const countryId = countryLookup.get(countryKey);

      return {
        // ---- date/rate core ----
        RateDateTime: todayISO,
        DateFrom: r.DateFrom ?? '',
        DateGoodThru: r.DateGoodThru ?? '',
        SteamshipRate: this.toNumberSafe(r.SteamshipRate, 0),

        // ---- schema fields ----
        DestinationPort: r.DestinationPort ?? '',
        DestinationID: this.toIntSafe(r.DestinationID, 0),
        DestinationType: r.DestinationType ?? '',
        Routing: r.Routing ?? '',

        OriginName: r.OriginName ?? '',
        CountryName: countryNameRaw ?? '',
        CountryID: countryId ?? 0,

        PreferredSSL: r.PreferredSSL ?? '',
        Contract: r.Contract ?? '',
        OriginPort: r.OriginPort ?? '',
        CNTR: r.CNTR ?? '',

        // ---- defaults/business flags ----
        PreferredRate: false,
        Approved: true,
        Active: 1
      };
    });

    // ✅ Step 1: apply Active flag
    const withActive = this.applyActiveFlag(uploaded);

    // ✅ Step 2: keep only active uploads
    const uploadedActive = withActive.filter(r => r.Active === 1);

    // ✅ Step 3: dedupe inside upload
    const dedupedUpload = this.dedupeList(uploadedActive);

    // ✅ Step 4: find brand-new rows (INSERT)
    const newRates = this.removeExistingRates(dedupedUpload, this.CurrentRates);

    // ✅ Step 5: find rows to deactivate (UPDATE)
    const toDeactivate = this.findRatesToDeactivate(this.CurrentRates, dedupedUpload);

    // ✅ Step B: rate variance check (Origin + Destination + WHSE)
    const variances = this.findWideVariances(dedupedUpload, this.CurrentRates, 500);
    if (variances.length) {
      const ok = await this.confirmWideVariances(variances);
      if (!ok) {
        this.message.error('Upload canceled. No changes were applied.');
        return;
      }
    }

    // ✅ Step 6: build FINAL merge payload
    const mergePayload = this.normalizeForMerge([
      ...toDeactivate,
      ...newRates.map(r => ({ ...r, Active: 1 }))
    ]);

    // ✅ Send to SQL
    await lastValueFrom(
      this.webService.getWebCall('inventory', 'merge_sslrates', { body: mergePayload })
    );

    await this.ngOnInit();
    this.message.success('Rates uploaded successfully');
  }

  // =========================
  // Excel validation (row/column)
  // =========================

  private formatIssues(issues: ImportIssue[], max = 15): string {
    const shown = issues.slice(0, max);
    const lines = shown.map(i =>
      `Row ${i.row}, "${i.column}": ${i.message}${i.value !== undefined ? ` (value: ${JSON.stringify(i.value)})` : ''}`
    );
    const more = issues.length > max ? `\n...and ${issues.length - max} more.` : '';
    return lines.join('\n') + more;
  }

  /** HeaderRow is auto; assume 1 header row => first data row is Excel row 2 */
  private excelRowNumber(rowIndex0: number, headerRows = 2): number {
    return rowIndex0 + 1 + headerRows;
  }

  private isBlank(v: any) {
    return v === null || v === undefined || String(v).trim() === '';
  }

  private isValidISODate(v: any): boolean {
    if (this.isBlank(v)) return false;

    if (v instanceof Date && !isNaN(v.getTime())) return true;

    const s = String(v).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;

    const d = new Date(s);
    return !isNaN(d.getTime());
  }

  private isFiniteNumber(v: any): boolean {
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n);
  }

  private toNumberSafe(v: any, fallback: number): number {
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : fallback;
  }

  private toIntSafe(v: any, fallback: number): number {
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isInteger(n) ? n : fallback;
  }

  private validateUploadedRows(rows: any[]): ImportIssue[] {
    const issues: ImportIssue[] = [];
    const headerRows = 2;

    rows.forEach((r, idx) => {
      const excelRow = this.excelRowNumber(idx, headerRows);

      // Required fields
      if (this.isBlank(r.PreferredSSL)) {
        issues.push({ row: excelRow, column: 'Carrier', message: 'Required value is missing', value: r.PreferredSSL });
      }

      if (!this.isValidISODate(r.DateFrom)) {
        issues.push({ row: excelRow, column: 'Validity From', message: 'Must be a valid date (YYYY-MM-DD)', value: r.DateFrom });
      }

      if (!this.isValidISODate(r.DateGoodThru)) {
        issues.push({ row: excelRow, column: 'Validity To', message: 'Must be a valid date (YYYY-MM-DD)', value: r.DateGoodThru });
      }

      if (this.isBlank(r.DestinationPort)) {
        issues.push({ row: excelRow, column: 'Destination', message: 'Required value is missing', value: r.DestinationPort });
      }

      if (!this.isFiniteNumber(r.SteamshipRate)) {
        issues.push({ row: excelRow, column: 'Base Freight', message: 'Must be a number', value: r.SteamshipRate });
      } else {
        const n = this.toNumberSafe(r.SteamshipRate, 0);
        if (n < 0) issues.push({ row: excelRow, column: 'Base Freight', message: 'Cannot be negative', value: r.SteamshipRate });
      }

      if (this.isBlank(r.OriginName)) {
        issues.push({ row: excelRow, column: 'Country of Origin', message: 'Required value is missing', value: r.OriginName });
      }

      if (this.isBlank(r.CountryName)) {
        issues.push({ row: excelRow, column: 'Country', message: 'Required value is missing', value: r.CountryName });
      }

      const destId = typeof r.DestinationID === 'number' ? r.DestinationID : parseFloat(String(r.DestinationID));
      if (!Number.isInteger(destId)) {
        issues.push({ row: excelRow, column: 'Destination WHSE', message: 'Must be an integer', value: r.DestinationID });
      }

      // Date sanity: Thru must be >= From
      if (this.isValidISODate(r.DateFrom) && this.isValidISODate(r.DateGoodThru)) {
        const from = new Date(String(r.DateFrom));
        const thru = new Date(String(r.DateGoodThru));
        if (thru < from) {
          issues.push({
            row: excelRow,
            column: 'Validity To',
            message: '"Validity To" must be on/after "Validity From"',
            value: r.DateGoodThru
          });
        }
      }
    });

    return issues;
  }

  // =========================
  // Wide variance check (compare to existing)
  // =========================

  private rateCompareKey(r: any): string {
    return [
      this.normText(r?.OriginName),
      this.normText(r?.DestinationPort),
      String(r?.DestinationID ?? '')
    ].join('|');
  }

  private findBestExistingMatch(uploadRow: any, existing: any[]): any | null {
    const key = this.rateCompareKey(uploadRow);
    const matches = (existing ?? [])
      .filter(e => e.Active === 1)
      .filter(e => this.rateCompareKey(e) === key);

    if (!matches.length) return null;

    // Pick “most recent” by DateFrom then RateDateTime
    matches.sort((a, b) =>
      String(b.DateFrom ?? '').localeCompare(String(a.DateFrom ?? '')) ||
      String(b.RateDateTime ?? '').localeCompare(String(a.RateDateTime ?? ''))
    );

    return matches[0];
  }

  private findWideVariances(uploadedRows: any[], existing: any[], threshold = 1000): RateVariance[] {
    const variances: RateVariance[] = [];
    const headerRows = 2;

    uploadedRows.forEach((u, idx) => {
      const ex = this.findBestExistingMatch(u, existing);
      if (!ex) return;

      const up = Number(u.SteamshipRate ?? 0);
      const cur = Number(ex.SteamshipRate ?? 0);
      const diff = Math.abs(up - cur);

      if (diff > threshold) {
        variances.push({
          row: this.excelRowNumber(idx, headerRows),
          origin: String(u.OriginName ?? ''),
          destination: String(u.DestinationPort ?? ''),
          whse: Number(u.DestinationID ?? 0),
          uploaded: up,
          existing: cur,
          diff,
          existingId: ex.OriginRateID
        });
      }
    });

    return variances;
  }

  private async confirmWideVariances(variances: RateVariance[]): Promise<boolean> {
    const sample = variances.slice(0, 8)
      .map(v =>
        `Row ${v.row}: ${v.origin} -> ${v.destination} (WHSE ${v.whse}) uploaded ${v.uploaded} vs existing ${v.existing} (Δ ${v.diff})`
      )
      .join('\n');

    return await new Promise<boolean>((resolve) => {
      const { closed$ } = this.prompt.open({
        promptText:
          `Some uploaded rates differ by more than $500 from existing saved rates.\n\n` +
          `${sample}\n\n` +
          `Type CONTINUE to proceed anyway.`,
        promptInput: 'text',
        inputLabel: 'Type CONTINUE to proceed',
        inputRequired: true,
        buttons: [{ value: 1, label: 'Continue', buttonClass: 'btn-bg-green' }]
      });

      closed$.subscribe(res => {
        const ok = !!res && String(res.inputValue || '').trim().toUpperCase() === 'CONTINUE';
        resolve(ok);
      });
    });
  }

  // =========================
  // Active flag logic
  // =========================

  private todayMidnight(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private parseDateOnly(v: any): Date | null {
    if (!v) return null;

    if (v instanceof Date && !isNaN(v.getTime())) {
      const d = new Date(v);
      d.setHours(0, 0, 0, 0);
      return d;
    }

    const s = String(v).trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) {
      const d = new Date(+m[1], +m[2] - 1, +m[3]);
      d.setHours(0, 0, 0, 0);
      return d;
    }

    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  }

  /**
   * ✅ Sets Active = 1 or 0 based on DateGoodThru
   */
  private applyActiveFlag<T extends { DateGoodThru?: any }>(list: T[]): (T & { Active: number })[] {
    const today = this.todayMidnight();

    return (list ?? []).map(item => {
      const goodThru = this.parseDateOnly(item.DateGoodThru);
      const active = !!goodThru && goodThru >= today ? 1 : 0;
      return { ...item, Active: active };
    });
  }

  // =========================
  // Normalization
  // =========================

  private normalizeRow(r: any): any {
    return {
      OriginRateID: r.OriginRateID,

      // keep both CountryName + CountryID
      CountryName: String(r?.CountryName ?? '').trim(),
      CountryID: Number(r?.CountryID ?? 0) || 0,

      // keep these for comparison and identity keys
      OriginName: String(r?.OriginName ?? '').trim(),
      OriginPort: String(r?.OriginPort ?? '').trim(),
      DestinationID: Number(r?.DestinationID ?? 0) || 0,
      DestinationPort: String(r?.DestinationPort ?? '').trim(),
      DestinationType: String(r?.DestinationType ?? '').trim(),
      Routing: String(r?.Routing ?? '').trim(),
      CNTR: String(r?.CNTR ?? '').trim(),
      Contract: String(r?.Contract ?? '').trim(),

      // legacy field (if your API returns it)
      Destination: String(r?.Destination ?? '').trim(),

      RateDateTime: this.toISO(r?.RateDateTime) ?? '',
      DateFrom: this.toISO(r?.DateFrom) ?? '',
      DateGoodThru: this.toISO(r?.DateGoodThru) ?? '',
      SteamshipRate: Number.isFinite(+r?.SteamshipRate) ? +r.SteamshipRate : 0,

      PreferredSSL: String(r?.PreferredSSL ?? '').trim(),
      PreferredRate: !!r?.PreferredRate,
      Approved: !!r?.Approved,

      Active:
        r?.Active === 1 || r?.Active === true || r?.Active === '1'
          ? 1
          : 0
    };
  }

  private safeDate(v: any): Date | null {
    const d = v instanceof Date ? v : new Date(v);
    return isNaN(d.getTime()) ? null : this.midnight(d);
  }

  private midnight(d: Date) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  private toISO(v: any): string | null {
    const d = this.safeDate(v);
    return d
      ? new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
        .toISOString()
        .substring(0, 10)
      : null;
  }

  private normText(v: any): string {
    return String(v ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  private buildCountryLookup(): Map<string, number> {
    const map = new Map<string, number>();

    for (const c of (this.CountriesList ?? [])) {
      const name = this.normText(c?.CountryName);
      const id = Number(c?.CountryID);

      if (name && Number.isFinite(id)) {
        map.set(name, id);
      }
    }

    return map;
  }

  private normalizeForMerge(rows: any | any[]): any[] {
    const list = Array.isArray(rows) ? rows : [rows];

    return list.map(r => ({
      OriginRateID: r.OriginRateID ?? null,
      RateDateTime: r.RateDateTime ?? null,
      CountryID: r.CountryID ?? null,
      SteamShipRate: r.SteamshipRate ?? null,
      PreferredSSL: r.PreferredSSL ?? null,
      Comments: r.Comments ?? null,
      PreferredRate: r.PreferredRate ?? null,
      DateFrom: r.DateFrom ?? null,
      DateGoodThru: r.DateGoodThru ?? null,
      Buffer: 0.025,
      AddedBuffer: 0.025,
      DestinationID: r.DestinationID ?? null,
      CNTR: r.CNTR ?? null,
      Term: r.Term ?? null,
      Active: r.Active ?? null,
      Approved: r.Approved ?? null,
      Contract: r.Contract ?? null,
      OriginName: r.OriginName ?? null,
      OriginPort: r.OriginPort ?? null,
      DestinationPort: r.DestinationPort ?? null,
      DestinationType: r.DestinationType ?? null,
      Routing: r.Routing ?? null
    }));
  }

  /**
   * IMPORTANT:
   * This key is used to detect duplicates and whether uploaded rows already exist.
   * Do NOT include OriginRateID here, because uploaded rows won't have it.
   */
  private buildRateIdentityKey(r: any): string {
    const keyObj = {
      CountryID: r.CountryID ?? null,
      OriginName: r.OriginName ?? null,
      OriginPort: r.OriginPort ?? null,
      DestinationID: r.DestinationID ?? null,
      DestinationPort: r.DestinationPort ?? null,
      DestinationType: r.DestinationType ?? null,
      Routing: r.Routing ?? null,
      CNTR: r.CNTR ?? null,
      Contract: r.Contract ?? null,
      DateFrom: r.DateFrom ?? null,
      DateGoodThru: r.DateGoodThru ?? null,
      SteamshipRate: r.SteamshipRate ?? null,
      PreferredSSL: r.PreferredSSL ?? null,
      PreferredRate: r.PreferredRate ?? null,
      Buffer: 0.025,
      AddedBuffer: 0.025,
      Active: r.Active ?? null,
      Approved: r.Approved ?? null
    };

    return JSON.stringify(keyObj);
  }

  private dedupeList(list: any[]): any[] {
    const seen = new Set<string>();
    return list.filter(r => {
      const key = this.buildRateIdentityKey(r);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private removeExistingRates(uploaded: any[], existing: any[]): any[] {
    const existingKeys = new Set(
      (existing ?? []).map(r => this.buildRateIdentityKey(r))
    );

    return uploaded.filter(r => !existingKeys.has(this.buildRateIdentityKey(r)));
  }

  private findRatesToDeactivate(existing: any[], uploadedActive: any[]): any[] {
    const uploadedKeys = new Set(
      uploadedActive.map(r => this.buildRateIdentityKey(r))
    );

    return existing
      .filter(r => r.Active === 1) // only currently-active DB rows
      .filter(r => !uploadedKeys.has(this.buildRateIdentityKey(r)))
      .map(r => ({
        OriginRateID: r.OriginRateID,
        Active: 0
      }));
  }

  // =========================
  // Manual edit flow
  // =========================

  async UpdateRowRate(row: any) {
    const { closed$ } = this.prompt.open({
      promptText: `Current SSL Rate: ${this.formatMoney(row.SteamshipRate)}. Please Update With New Value`,
      promptInput: 'text',
      inputLabel: 'SSL Rate',
      inputRequired: true,
      buttons: [{ value: 1, label: "Update SSL Rate", buttonClass: 'btn-bg-green' }]
    });

    closed$.subscribe(async res => {
      if (!res) return;

      if (!parseFloat(res.inputValue)) {
        window.alert("Please enter a proper number for this value");
        return;
      }

      row.SteamshipRate = res.inputValue;

      const obj = {
        SteamShipRate: res.inputValue,
        OriginRateID: row.OriginRateID
      };

      const body = [obj];

      await lastValueFrom(this.webService.getWebCall("inventory", "merge_sslrates", { body }));
      await this.ngOnInit();
      this.message.success('Steamship Rate updated successfully');
    });
  }

  private formatMoney(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }
}
